import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  BarChart2,
  Trophy,
  Clock,
  Repeat,
  Brain,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthenticationProvider";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const RuleShifterMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="set-shifting"
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "set-shifting", name: "Memory Matrix" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={RuleShifterMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const RuleShifterMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
  onExit,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Game states
  const [gameMode, setGameMode] = useState("play");
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Timer (game duration)
  const [timeLeft, setTimeLeft] = useState(600); // e.g., 10 minutes
  const timerRef = useRef(null);

  // Game variables
  const [score, setScore] = useState(0);
  const [currentRound, setCurrentRound] = useState(0);
  const totalRounds = 40; // Fixed number of rounds for the match
  const [currentRule, setCurrentRule] = useState(null);
  const [previousRule, setPreviousRule] = useState(null);
  const [rulesDiscovered, setRulesDiscovered] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [currentCard, setCurrentCard] = useState(null);
  const [optionCards, setOptionCards] = useState([]);
  const [perseverationErrors, setPerseverationErrors] = useState(0);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [shiftingErrors, setShiftingErrors] = useState(0);
  const [averageResponseTime, setAverageResponseTime] = useState(0);
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);
  const [ruleChangeCount, setRuleChangeCount] = useState(0);

  // Game configuration
  const shapes = ["circle", "square", "triangle", "diamond"];
  const colors = ["red", "blue", "green", "yellow"];
  const counts = [1, 2, 3, 4];
  const fills = ["solid", "outline", "striped", "dotted"];
  const [difficulty, setDifficulty] = useState("standard");

  const difficultySettings = {
    easy: {
      rulesBeforeChange: 8,
      optionCount: 3,
      responseTimeLimit: 10000,
      hintAvailable: true,
    },
    standard: {
      rulesBeforeChange: 6,
      optionCount: 4,
      responseTimeLimit: 8000,
      hintAvailable: false,
    },
    advanced: {
      rulesBeforeChange: 5,
      optionCount: 4,
      responseTimeLimit: 6000,
      hintAvailable: false,
      noFeedback: true,
    },
  };

  // Possible rules
  const possibleRules = [
    { name: "color", property: "color" },
    { name: "shape", property: "shape" },
    { name: "count", property: "count" },
    { name: "fill", property: "fill" },
  ];

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // MULTIPLAYER SYNCHRONIZATION
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }
    }
  }, [gameState, gameStarted, isPlaying, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  // UTILITY FUNCTIONS
  // Generate a random card with given properties
  const generateRandomCard = (excludeProps = {}) => {
    let availableShapes = [...shapes];
    let availableColors = [...colors];
    let availableCounts = [...counts];
    let availableFills = [...fills];

    if (excludeProps.shape)
      availableShapes = availableShapes.filter((s) => s !== excludeProps.shape);
    if (excludeProps.color)
      availableColors = availableColors.filter((c) => c !== excludeProps.color);
    if (excludeProps.count)
      availableCounts = availableCounts.filter((c) => c !== excludeProps.count);
    if (excludeProps.fill)
      availableFills = availableFills.filter((f) => f !== excludeProps.fill);

    const shape =
      availableShapes[Math.floor(Math.random() * availableShapes.length)];
    const color =
      availableColors[Math.floor(Math.random() * availableColors.length)];
    const count =
      availableCounts[Math.floor(Math.random() * availableCounts.length)];
    const fill =
      availableFills[Math.floor(Math.random() * availableFills.length)];

    return { shape, color, count, fill };
  };

  // Generate option cards
  const generateOptions = (rule, mainCard, optionCount) => {
    const correctOption = { ...generateRandomCard() };
    correctOption[rule.property] = mainCard[rule.property];
    const options = [correctOption];

    while (options.length < optionCount) {
      const newOption = generateRandomCard();
      if (newOption[rule.property] !== mainCard[rule.property]) {
        const duplicate = options.some(
          (opt) =>
            opt.shape === newOption.shape &&
            opt.color === newOption.color &&
            opt.count === newOption.count &&
            opt.fill === newOption.fill
        );
        if (!duplicate) {
          options.push(newOption);
        }
      }
    }
    return options.sort(() => Math.random() - 0.5);
  };

  // Select a new rule (different from the previous one)
  const selectNewRule = () => {
    let availableRules = [...possibleRules];
    if (previousRule) {
      availableRules = availableRules.filter(
        (r) => r.property !== previousRule.property
      );
    }
    const newRule =
      availableRules[Math.floor(Math.random() * availableRules.length)];
    return newRule;
  };

  // Start the game with countdown
  const startGame = () => {
    console.log("Starting countdown");
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        console.log("Countdown tick:", prev);
        if (prev <= 1) {
          console.log("Countdown finished");
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    console.log("Starting game after countdown");
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setCurrentRound(0);
    setConsecutiveCorrect(0);
    setRulesDiscovered(0);
    setPerseverationErrors(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setShiftingErrors(0);
    setResponseTimes([]);
    setAverageResponseTime(0);
    setRuleChangeCount(0);
    setGameStartTime(Date.now());
    setGameEndTime(null);

    // IMPORTANT: Make these separate state updates
    setIsPlaying(true);
    setGameStarted(true);

    // Set time limit
    setTimeLeft(difficulty === "easy" ? 480 : 600);

    // Start the timer countdown
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Select first rule
    const firstRule =
      possibleRules[Math.floor(Math.random() * possibleRules.length)];
    setCurrentRule(firstRule);
    setPreviousRule(null);

    // Generate first card
    const mainCard = generateRandomCard();
    setCurrentCard(mainCard);

    // Generate options
    const optionCount = difficultySettings[difficulty].optionCount;
    const options = generateOptions(firstRule, mainCard, optionCount);
    setOptionCards(options);

    // Set response time start
    setResponseStartTime(Date.now());
    setCurrentRound(1);

    // Update the game state at the end
    console.log("Updating game state to active");
    if (updateGameState) {
      updateGameState({
        status: "active",
        myProgress: 0,
        myScore: 0,
      });
    }
  };

  // Begin a new round
  const startNewRound = () => {
    console.log("Starting new round, currentRound:", currentRound);

    if (!isPlaying || currentRound >= totalRounds) {
      console.log("Not playing or reached total rounds, ending game");
      endGame();
      return;
    }

    const mainCard = generateRandomCard();
    setCurrentCard(mainCard);
    const optionCount = difficultySettings[difficulty].optionCount;
    const options = generateOptions(currentRule, mainCard, optionCount);
    setOptionCards(options);
    setResponseStartTime(Date.now());
    setCurrentRound((prev) => prev + 1);
  };

  // Check if it's time to change the hidden rule
  const checkRuleChange = () => {
    const threshold = difficultySettings[difficulty].rulesBeforeChange;
    if (consecutiveCorrect >= threshold) {
      const oldRule = currentRule;
      const newRule = selectNewRule();
      setConsecutiveCorrect(0);
      setCurrentRule(newRule);
      setPreviousRule(oldRule);
      setRuleChangeCount((prev) => prev + 1);
      setScore((prev) => prev + 50);
      setRulesDiscovered((prev) => prev + 1);
      return true;
    }
    return false;
  };

  // End the game
  const endGame = () => {
    console.log("Ending game");
    if (scoreSubmitted) {
      console.log("Score already submitted, skipping");
      return;
    }
    setScoreSubmitted(true);

    setIsPlaying(false);
    setGameMode("results");
    clearInterval(timerRef.current);
    setGameEndTime(Date.now());

    // Calculate avg response time
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Submit the score + detailed stats
    console.log("Submitting score and stats");
    const stats = {
      correctResponses,
      incorrectResponses,
      perseverationErrors,
      shiftingErrors,
      ruleChangeCount,
      rulesDiscovered,
      averageResponseTime: Math.round(
        responseTimes.length > 0
          ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
          : 0
      ),
      gameLength: gameEndTime - gameStartTime,
    };

    if (submitScore) {
      submitScore(score, stats);
    }

    // Update game state
    if (updateGameState) {
      updateGameState({
        myProgress: 1,
        myScore: score,
        myLastResponse: {
          gameComplete: true,
          finalStats: stats,
        },
      });
    }
  };

  // Handle user selecting an option
  const handleOptionSelect = (option) => {
    console.log("Option selected:", option);
    if (!isPlaying) return;

    const responseTime = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, responseTime]);

    const isCorrect =
      option[currentRule.property] === currentCard[currentRule.property];

    // Set feedback state
    setFeedbackCorrect(isCorrect);
    setShowFeedback(true);

    // Hide feedback after a delay
    setTimeout(() => {
      setShowFeedback(false);
    }, 800);

    if (isCorrect) {
      setConsecutiveCorrect((prev) => prev + 1);
      setCorrectResponses((prev) => prev + 1);

      // Score calculation based on response time
      let roundScore = 10;
      if (responseTime < 1000) {
        roundScore = 30;
      } else if (responseTime < 2000) {
        roundScore = 25;
      } else if (responseTime < 3000) {
        roundScore = 20;
      } else if (responseTime < 4000) {
        roundScore = 15;
      }

      setScore((prev) => prev + roundScore);
      checkRuleChange();

      // Update multiplayer state
      if (updateGameState) {
        setTimeout(() => {
          updateGameState({
            myProgress: currentRound / totalRounds,
            myScore: score + roundScore,
            myLastResponse: {
              response: true,
              reactionTime: responseTime,
            },
          });
        }, 0);
      }
    } else {
      setConsecutiveCorrect(0);
      setIncorrectResponses((prev) => prev + 1);

      if (
        previousRule &&
        option[previousRule.property] === currentCard[previousRule.property]
      ) {
        setPerseverationErrors((prev) => prev + 1);
        setShiftingErrors((prev) => prev + 1);
      }

      setScore((prev) => Math.max(0, prev - 10));

      // Update multiplayer state
      if (updateGameState) {
        setTimeout(() => {
          updateGameState({
            myProgress: currentRound / totalRounds,
            myScore: Math.max(0, score - 10),
            myLastResponse: {
              response: false,
              reactionTime: responseTime,
            },
          });
        }, 0);
      }
    }

    // Next round after short delay
    setTimeout(() => {
      if (currentRound < totalRounds) {
        startNewRound();
      } else {
        endGame();
      }
    }, 800);
  };

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  // RENDERING HELPERS
  const renderCard = (card, small = false) => {
    if (!card) return null;
    const shapesArr = [];
    for (let i = 0; i < card.count; i++) {
      shapesArr.push(renderShape(card, i, small));
    }
    return (
      <div
        className={`${
          small ? "w-32 h-32" : "w-48 h-48"
        } bg-slate-800 rounded-lg flex items-center justify-center p-4`}
      >
        <div className="flex flex-wrap justify-center items-center gap-2">
          {shapesArr}
        </div>
      </div>
    );
  };

  const renderShape = (card, key, small = false) => {
    const { shape, color, fill } = card;
    const size = small ? "w-10 h-10" : "w-16 h-16";
    const colorClasses = {
      red: fill === "solid" ? "bg-red-500" : "border-red-500 text-red-500",
      blue: fill === "solid" ? "bg-blue-500" : "border-blue-500 text-blue-500",
      green:
        fill === "solid" ? "bg-green-500" : "border-green-500 text-green-500",
      yellow:
        fill === "solid"
          ? "bg-yellow-500"
          : "border-yellow-500 text-yellow-500",
    };
    const shapeClasses = {
      circle: "rounded-full",
      square: "rounded-none",
      triangle: "triangle",
      diamond: "diamond",
    };
    const fillClasses = {
      solid: "border-0",
      outline: "border-2 bg-transparent",
      striped: "striped",
      dotted: "dotted",
    };
    return (
      <div
        key={key}
        className={`${size} ${shapeClasses[shape]} ${colorClasses[color]} ${fillClasses[fill]} border-2 flex items-center justify-center`}
      >
        {fill === "dotted" && (
          <div className="dot-pattern absolute inset-0"></div>
        )}
        {fill === "striped" && (
          <div className="stripe-pattern absolute inset-0"></div>
        )}
      </div>
    );
  };

  // RENDER COMPONENT
  return (
    <div className="bg-slate-900 text-white overflow-hidden flex flex-col h-full">
      {/* Header Bar - Compact like in your screenshots */}
      <div className="flex items-center justify-between p-3 bg-slate-800/60">
        <button
          onClick={() => navigate("/gameroom")}
          className="flex items-center gap-2 text-slate-300"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <div className="text-emerald-300 font-medium">Rule Shifter</div>
        <div className="w-6"></div> {/* Spacer for alignment */}
      </div>

      {/* Game Content - Fixed height with proper spacing */}
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        {/* Game Info Bar */}
        <div className="w-full flex justify-between mb-3">
          <div className="bg-slate-800 px-3 py-1 rounded-md">
            <span className="text-emerald-300 text-sm">
              Level: {difficulty}
            </span>
          </div>
          <div className="flex gap-2">
            <div className="bg-slate-800 px-3 py-1 rounded-md flex items-center">
              <Clock size={14} className="text-slate-400 mr-1" />
              <span className="text-white text-sm">{formatTime(timeLeft)}</span>
            </div>
            <div className="bg-slate-800 px-3 py-1 rounded-md">
              <span className="text-white text-sm">Score: {score}</span>
            </div>
          </div>
        </div>

        {/* Rules Progress Bar */}
        <div className="w-full mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-400">Rules Discovered</span>
            <span className="text-white">{rulesDiscovered}</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{
                width: `${Math.min((rulesDiscovered / 10) * 100, 100)}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Game Instruction */}
        <div className="text-white text-sm mb-2">Find the matching rule</div>

        {/* Main Card - Smaller with less padding */}
        <div className="w-44 h-44 bg-slate-800 rounded-md flex items-center justify-center mb-4">
          {currentCard && renderCard(currentCard, false)}
        </div>

        {/* Options - Simple grid like in screenshots */}
        <div className="bg-slate-800 w-full p-3 rounded-md mb-4">
          <div className="grid grid-cols-4 gap-2">
            {optionCards.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionSelect(option)}
                className="flex items-center justify-center"
                disabled={!isPlaying || showFeedback}
              >
                {renderCard(option, true)}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 w-full">
          <div className="bg-slate-800 p-2 rounded-md">
            <div className="text-slate-400 text-xs">Streak</div>
            <div className="text-white text-sm">{consecutiveCorrect}</div>
          </div>
          <div className="bg-slate-800 p-2 rounded-md">
            <div className="text-slate-400 text-xs">Round</div>
            <div className="text-white text-sm">
              {currentRound}/{totalRounds}
            </div>
          </div>
          <div className="bg-slate-800 p-2 rounded-md">
            <div className="text-slate-400 text-xs">Correct</div>
            <div className="text-green-400 text-sm">{correctResponses}</div>
          </div>
          <div className="bg-slate-800 p-2 rounded-md">
            <div className="text-slate-400 text-xs">Errors</div>
            <div className="text-red-400 text-sm">{incorrectResponses}</div>
          </div>
        </div>

        {/* End Game Button */}
        <button
          onClick={() => endGame()}
          className="mt-3 py-1.5 px-4 bg-slate-700 text-white text-sm rounded-md"
        >
          End Game
        </button>
      </div>

      {/* Bottom Help Bar - Fixed at bottom like in screenshots */}
      <div className="bg-slate-800 py-2 px-4 flex justify-between items-center">
        <span className="text-white text-xs">Streak: {consecutiveCorrect}</span>
        <span className="text-emerald-300 text-xs">Select a matching card</span>
      </div>

      {/* Waiting Overlay */}
      {!gameStarted && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90">
          {waitingForOpponent ? (
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-2 border-t-transparent border-emerald-500 rounded-full animate-spin mb-2"></div>
              <p className="text-white">Waiting for opponent...</p>
            </div>
          ) : (
            <button
              onClick={handleReadyClick}
              className="py-2 px-4 bg-emerald-500 text-white rounded-md"
            >
              Ready to Play
            </button>
          )}
        </div>
      )}

      {/* Countdown Overlay */}
      {showCountdown && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="text-5xl font-bold text-white mb-1">
              {countdown > 0 ? countdown : "GO!"}
            </div>
            <div className="text-slate-300">Get ready...</div>
          </div>
        </div>
      )}

      {/* Feedback Overlay */}
      {showFeedback && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
          <div className="bg-slate-800 p-4 rounded-md text-center">
            <div
              className={`text-xl font-bold ${
                feedbackCorrect ? "text-green-400" : "text-red-400"
              }`}
            >
              {feedbackCorrect ? "Correct!" : "Incorrect"}
            </div>
            {feedbackCorrect &&
              consecutiveCorrect >=
                difficultySettings[difficulty].rulesBeforeChange - 1 && (
                <p className="text-emerald-300 text-sm">Rule Changed!</p>
              )}
          </div>
        </div>
      )}

      {/* Results Screen */}
      {!isPlaying && gameState && gameState.status === "completed" && (
        <div className="absolute inset-0 bg-slate-900 flex flex-col">
          <div className="p-4 flex-1 overflow-y-auto">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-emerald-500 rounded-full mx-auto flex items-center justify-center mb-2">
                <Trophy size={28} className="text-white" />
              </div>
              <h2 className="text-lg font-bold text-white">Game Complete!</h2>
              <p className="text-slate-300 text-sm">
                You scored {score} points
              </p>
            </div>

            <div className="bg-slate-800 rounded-md p-3 mb-3">
              <h3 className="text-white text-sm font-medium mb-2">Results</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-300">Score:</span>
                  <span className="text-emerald-300 font-bold">{score}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Rules Found:</span>
                  <span className="text-white">{rulesDiscovered}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Correct:</span>
                  <span className="text-green-400">{correctResponses}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Errors:</span>
                  <span className="text-red-400">{incorrectResponses}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-2">
              <button
                onClick={() => submitScore(score)}
                className="py-1.5 px-3 bg-emerald-500 text-white text-sm rounded-md"
              >
                Save Score
              </button>
              <button
                onClick={() => {
                  setIsPlaying(true);
                  startGameAfterCountdown();
                }}
                className="py-1.5 px-3 bg-slate-700 text-white text-sm rounded-md"
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for shapes */}
      <style>
        {`
          .triangle { clip-path: polygon(50% 0%, 0% 100%, 100% 100%); }
          .diamond { clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%); }
          .striped { background-image: repeating-linear-gradient(45deg, currentColor, currentColor 2px, transparent 2px, transparent 6px); }
          .dotted { background-image: radial-gradient(currentColor 15%, transparent 15%); background-size: 6px 6px; }
        `}
      </style>
    </div>
  );
};

export default RuleShifterMultiWrapper;
